<template>
  <div v-if="isInrCurrency && countryCode === 'IND'" class="my-4 flex flex-wrap items-start gap-x-3 gap-y-2 @container">
    <VcCheckbox
      v-model="registered"
      :disabled="disabled"
      :name="$t('pages.checkout.gst_registration')"
      class="flex min-h-12 items-center text-base font-semibold"
    >
      {{ $t("pages.checkout.gst_registration") }}
    </VcCheckbox>
    <div v-if="registered" class="w-full max-w-full shrink-0 md:@lg:w-64">
      <VcInput
        :model-value="modelValue ?? ''"
        :disabled="disabled"
        :maxlength="15"
        :placeholder="$t('pages.checkout.gstin')"
        :aria-label="$t('pages.checkout.gstin')"
        :error="invalid"
        :aria-invalid="invalid"
        :aria-describedby="invalid ? warningId : undefined"
        autocomplete="off"
        @update:model-value="emit('update:modelValue', $event ?? '')"
      />
      <p
        v-if="registered && invalid"
        :id="warningId"
        class="mt-1 w-full text-sm text-[--color-danger]"
        aria-live="polite"
      >
        {{ $t("pages.checkout.invalid_gstin") }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useCurrency } from "@/core/composables/useCurrency";
import { isValidGstin } from "@/shared/checkout/utils/gstin";
import { useComponentId } from "@/ui-kit/composables";
import VcCheckbox from "@/ui-kit/components/atoms/checkbox/vc-checkbox.vue";
import VcInput from "@/ui-kit/components/molecules/input/vc-input.vue";

const emit = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();
const props = defineProps<{
  modelValue?: string | null;
  countryCode?: string;
  disabled?: boolean;
}>();

const { currentCurrency } = useCurrency();
const isInrCurrency = computed(() => currentCurrency.value.code === "INR");
const checked = ref(false);
const warningId = useComponentId("gstin-warning");
const registered = computed({
  get: () => checked.value,
  set: (value: boolean) => {
    checked.value = value;
    emit("update:modelValue", "");
  },
});
const invalid = computed(() => !!props.modelValue && !isValidGstin(props.modelValue));

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      checked.value = true;
    }
  },
  { immediate: true },
);
watch(
  () => props.countryCode,
  () => {
    if (isInrCurrency.value) {
      checked.value = false;
      emit("update:modelValue", "");
    }
  },
);
</script>
